<!-- Skip link — WCAG 2.4.1 Bypass Blocks. FIRST focusable element on the
     page: hidden until keyboard focus, then jumps past the banner stack +
     glass bar + panels straight into the routed content. imSkipLink moves
     focus programmatically because a bare href fragment would be parsed as
     a route under hash routing. -->
<a
  imSkipLink="main-content"
  class="visually-hidden-until-focus ph-no-capture"
  data-testid="app-shell-skip-link">
  {{ 'shell.a11y.skip_to_main' | transloco }}
</a>

<!-- Onboarding overlay — renders OUTSIDE the shell to avoid transform stacking context issues.
     NOT on a /preview/* harness: those are opened cold by somebody who is not
     signed in, so onboarding is never complete for them and the tour covered
     every surface under review. See `isPreviewHarness`. -->
@if (!onboardingComplete() && !isPreviewHarness()) {
  <!-- Deferred: first run (Paper's onboarding, its emblems and copy) is its
       own chunk, fetched only by someone who has not finished it. -->
  @defer (on immediate) {
    <im-first-run />
  }
}

<!-- ── Top banner stack ──────────────────────────────────────────────
     ALL fixed top-of-screen status banners share this single fixed
     container so they stack vertically instead of overlapping at top:0.
     The stack's measured height is published as --top-banner-stack-height
     (see setupBannerStackMeasurement) and the shell chrome (.app-shell,
     paper-glass-bar) offsets by it, so content is never hidden behind the
     banners. Each banner renders as a full-width strip via the
     .top-banner-stack ::ng-deep .paper-banner rules — NOT [fixedTop], so
     they flow within the column. Order = priority (connection → content
     blocker → delegation → update). -->
<div class="top-banner-stack" #topBannerStack>

  <!-- Connection banner — only shows when the API is unreachable. -->
  <paper-connection-banner />

  @if (!isPreviewHarness()) { <im-coexistence-entry /> }

  <!-- Content blocker warning — dismissible per session -->
  @if (shell.showAdBlockerBanner()) {
    <paper-banner
      tone="warning"
      icon="alert-triangle"
      [compact]="true"
      [dismissible]="true"
      [dismissLabel]="'shell.app_shell.dismiss_adblocker_banner' | transloco"
      data-testid="adblocker-banner"
      (dismissed)="shell.dismissAdBlockerBanner()">
      <span>{{ 'shell.app_shell.a_content_blocker_is_active_and' | transloco }}</span>
    </paper-banner>
  }

  <!-- Booking on behalf of someone is no longer a strip here: it floats at the
       top centre, under the search (im-delegation-pill, below), and pushes
       nothing down. -->

  <!-- App-update banner — a newer build was deployed under this open tab.
       Refresh reloads onto the fresh index.html + chunk map, avoiding the
       stale-chunk half-load that otherwise forces a manual hard refresh. -->
  @if (appUpdate.updateAvailable()) {
    <paper-banner
      tone="brand"
      icon="refresh"
      [compact]="true"
      ariaLive="polite"
      data-testid="app-update-banner">
      <span>{{ 'shell.app_shell.update_available' | transloco }}</span>
      <paper-button
        class="ph-no-capture"
        intent="primary"
        size="xs"
        [ariaLabel]="'shell.app_shell.update_refresh_aria' | transloco"
        (click)="applyAppUpdate()">{{ 'shell.app_shell.update_refresh' | transloco }}</paper-button>
    </paper-banner>
  }

</div>

<!-- THE PAGE DOES NOT MOVE WHEN A COLUMN OPENS. THE NAV BAR ACKNOWLEDGES IT.
     Those are two different statements, and conflating them is what cost the
     notch.

     WHAT STAYS DELETED: `--right-panel-inset`, and the
     `padding-inline-end` on `.main-surface` that read it. A column is an
     overlay. `.main-surface` is the same box, at the same x, at the same
     width, whether a column is up or not — measured both ways in this lane's
     report, because an invariant nobody measured is a hope.

     WHAT COMES BACK: §14.2's NOTCH, and only the notch. `--panel-clear` is
     published here from render (§14.7) and is read by exactly two rules in
     the SCSS — the `::before` that repaints the strip of bar above the
     column, and the ceiling that stops the page's own view tabs at the
     column's left edge. Neither moves a single pixel of page content; they
     make the bar and the panel read as one continuous surface, which is what
     the design draws and what the build's hard seam did not.

     `panelClear()` is `0px` unless a COLUMN is open — never for the profile
     popover, which hangs beneath the bar rather than meeting it, and never
     below the narrow breakpoint, where the column is a full-width sheet and
     there is no bar left to notch (§14.5d). -->
<div class="app-shell"
  [style.--panel-clear]="panelClear()"
  [attr.data-right-panel]="shell.rightPanel()"
  [attr.data-admin-scope]="isInAdmin() ? '' : null"
  [attr.data-workspace-nav]="workspaceNavPlacement()"
  [attr.data-bottom-nav]="showBottomNav() ? '' : null"
  [class.left-sidebar-open]="shell.leftSidebarOpen()"
  [class.delegation-active]="delegation.state().active"
  [class.home-glass-active]="currentGlassVariant() === 'home'"
  [class.game-play-active]="shell.isGamePlay()">

  <!-- Right panel slot — Workplace AI, Flow Intelligence (which itself wraps
       the Flow chat + Workplace AI behind a mode switcher), or the
       notification centre. Single-tenant: only one panel mounts at a time,
       which is what stops the bell and the avatar both opening a panel, and
       is §5 rule 4's one flag to clear.

       ⚠️ PROFILE IS NOT IN THIS SLOT ANY MORE, and that is a FAMILY change,
       not a move. `POPOVERS.md` §1: identity, presence, a couple of links —
       seconds, one choice, gone — is a POPOVER; a place you work for minutes
       with the page still live is a COLUMN. Profile was built here as a
       full-height right column (build 15) against a design that draws it
       anchored under the avatar (design 17). It now mounts at the bottom of
       this template as a band popover. `slotTenant()` is `rightPanel()`
       with `'profile'` mapped away so this @switch cannot render it, and
       `columnOpen()` is the same exclusion for every rule that means "a
       COLUMN is up" — the notch, the registry and the slot's focus
       management. One derivation, read by all three (§14.5a).

       §5.5 is owned HERE, not by the four panels, because the slot is what
       mounts and unmounts them: `tabindex="-1"` makes the container
       programmatically focusable, `manageSlotFocus()` moves focus in on open
       and hands it back to the trigger on close, and the Escape handler
       below closes whatever is in the slot. Doing it four times, four
       slightly different ways, is what this section exists to stop.

       DELIBERATELY NOT A FOCUS TRAP. The slot is `aria-modal="false"` — the
       page behind stays live — and it is the FIRST child of the shell, so
       Tab out of its last control lands in the bar and Shift+Tab out of its
       first lands on the skip link. There is a way out in both directions,
       which is the whole of WCAG 2.1.2. -->
  <aside
    class="right-panel"
    #rightPanelSlot
    tabindex="-1"
    [class.open]="columnOpen()"
    [class.right-panel--launch]="askLaunch.launching()">
    @switch (slotTenant()) {
      <!-- Grows out of its trigger when one hinted it (AskPanelLaunchService).
           Both Ask panels are @defer'd: the Ask kit loads on first open, not in
           the initial bundle, and the constructor warms its chunk once idle. -->
      @case ('ai') { @defer (on immediate) { <im-ai-panel (animate.enter)="aiPanelEnter($event)" /> } }
      @case ('flow-ai') { @defer (on immediate) { <im-flow-intelligence-panel /> } }
      <!-- The bell's Inbox: the same list as Home's Inbox tool (shared/inbox). -->
      @case ('notifications') { <im-inbox-column /> }
    }
    <!-- A colleague (PersonPanelService) lies OVER the tenant rather than replacing it, so "‹ Ask" finds the same
         conversation at the same scroll. Alone in the slot it is the 'person' tenant. -->
    @if (personPanel.showing()?.hostId === 'shell') { <im-person-panel class="right-panel__person" /> }
  </aside>

  <!-- Main surface — it does NOT resize when a panel opens. It used to pad by
       the open column's width, which slid the whole page left; a column is an
       overlay now and this stays exactly where it is underneath one.
       id + tabindex are the skip link's landing target (tabindex="-1" =
       programmatically focusable without joining the tab order). -->
  <main
    id="main-content"
    tabindex="-1"
    class="main-surface"
    [attr.data-section]="shell.currentSection()"
    [class.page-enter]="transitioning()">

    <!-- Navigation organism — Ricoh Paper's <paper-shell-bar>, which
         composes <paper-glass-bar layout="edge"> internally. Whether it
         renders at all is REGISTRY DATA, not a list of section names in this
         template: `SECTION_CHROME` says which sections run their own floating
         chrome or take the full surface, and `CHROME_TAKEOVERS` says which
         routes draw this same cluster themselves (where two live copies would
         share one ShellStateService signal and one NotificationCenterService).
         See `showShellBar()`.

         THE SHELL IMPLEMENTS NO NAVIGATION. It hands the organism the
         registry projections (workspace / workspaces / views) and routes the
         outputs back through the registry. The shed ladder, tab overflow +
         scroll-into-view, roving tabindex, the ⌥ keycap reveal, the overflow
         menu's includeCore rule and avatar containment are the organism's,
         and are covered by its own behavioural suite — none of it is
         reimplemented here.

         `context` is the named entity the URL is inside — a community or a
         colleague today. Supply one and the bar collapses its tiles to a
         pill, hosts the crumb (mark, name, back arrow) and swaps in the
         context's own views; `history` is what makes the back arrow appear
         at all, and it is empty on a cold deep link BY DESIGN — back
         retraces a path, and there is none. Search / AI /
         notifications / avatar are switched OFF on the bar and projected
         into [shell-bar-utility] instead, because this app's versions carry
         behaviour the bar's defaults do not (admin-scoped placeholder, Flow
         variant, the anchored notification panel, the avatar's status and
         badge dots). ⌘K / ⌘J stay owned by HotkeysService — the bar's own
         emissions for them are deliberately unbound, or the keystroke would
         toggle twice. -->
    <!-- ── LOUIS'S WORKSPACE NAVIGATION ─────────────────────────────────
         `im-workspace-nav` (the soft-frost rail and the app menu) stands in
         for the Paper bar wherever `workspaceNavShown()` — the Home workspace
         and Flow always, and with the `workspace-nav` flag every other employee route the
         registry allows (`SECTION_CHROME.workspaceNav`): over a page, or as
         glass over the map. It publishes the bar's offsets itself, so every
         surface that seats under `--shell-sticky` / `--immersive-chrome-top`
         clears it unchanged.

         Loaded on demand (`mountWorkspaceNav`): with the flag off only the
         Home and Flow workspaces draw it, so it, its rail and its icons stay out of
         the initial bundle. A plain dynamic import rather than @defer, whose
         runtime would itself join the initial bundle. -->
    @if (workspaceNavShown()) {
      <ng-container #workspaceNavHost />
    }
    @if (showShellBar()) {
    <!-- ── The location chip is PERSISTENT ────────────────────────────────
         `HANDOFF.md §2.5` makes location global context in the utility
         cluster, and `47-location-chip` draws it as Paper's own
         `paper-context-chip`: pin, site name, chevron. It used to render on
         the map ALONE, so everywhere else the bar carried no site at all and
         the only building affordance was the org switcher's unlabelled mark —
         a different control, switching a different thing.

         It is global now because the surfaces that read it are global.
         Discover's front page eyebrow, the composer's "Posting to …" and the
         pulse card all resolve through `ProfileDataService.locationName()`,
         which is `LocationStateService.selectedLocationId()` and nothing
         else; Home's week and the desk row read the same atom. A user who
         can SEE "posting to Birmingham" and cannot change it from the same
         chrome is being shown a fact with no handle on it.

         ONE SOURCE. This is a VIEW of `LocationStateService` —
         `MapContextChipService` owns no truth, and the menu below writes
         back through `LocationOrchestratorService`. There is no second store
         here and there must never be one.

         Two rules, in the order they are asked:

           1. ADMIN and the compact bar get `null`, which renders NOTHING.
              `/settings/*` consumes no site, and below the narrow breakpoint
              the cluster is already over-subscribed (`navReveal()` is `pill`
              for exactly that reason, and `chipFits()` in the component is
              the same predicate) — buildings stay reachable there through
              ⌘K and the bottom nav. Hoisting both guards to the FRONT
              changes nothing on the map, where `chipLocation()` was already
              null under `pill`; it just makes the deliberate nulls the only
              nulls left.

           2. Otherwise the chip is ALWAYS rendered:
                - MAP keeps `chipLocation()` verbatim while it can answer —
                  the map's chip carries the FLOOR after a hairline
                  (`BHM │ L1`), which lives in the component and stays
                  exactly as shipped;
                - everywhere else, and on the map while `chipLocation()`
                  cannot answer, `chip.barSite()` — the same site WITHOUT
                  `detail` (off the map there is no floor selection on
                  screen, so a floor token would assert a context the page
                  does not have), falling back to an honest stand-in.

         PERSISTENT MEANS IT DOES NOT VANISH WHILE DATA SETTLES, and that is
         the bug this arm fixes. Paper renders the chip `@if (location(); as
         site)`, and `chip.activeSite()` resolves through
         `sites().find(id === selectedLocationId)` — so before the estate
         query returns, or whenever the selected id is not in the list it
         returned, the bar rendered no chip at all. `barSite()` never returns
         null; it reads "Loading…" while a load is genuinely in flight and
         "Choose a site" otherwise. It does NOT invent a location name — see
         the long note on `MapContextChipService.barSite`.

         `locationExpanded` / `timeExpanded` below are the other half of the
         same handover, and were previously unbound. The chip is a TRIGGER —
         it hands the host an element and the host opens its own panel — so
         the chip cannot know whether its popup is up. A bar that does not
         forward that publishes `aria-expanded="false"` for as long as the
         menu is open, which is a lie a screen-reader user acts on and is not
         fixable from here: the attribute is inside the chip.

         OWED, and deliberately not done from a template: fold this back into
         `chipLocation()` in `app-shell.component.ts` — that file is outside
         this lane, so the expression lands here rather than nowhere. -->
    @let barLocation = isInAdmin() || navReveal() === 'pill'
      ? null
      : (isMapSurface() ? (chipLocation() ?? chip.barSite()) : chip.barSite());
    <!-- imDesktopDragRegion: on the macOS desktop app the bar's empty surfaces
         move the window and a double-click zooms it. A bare attribute on this
         host never fired: the host is a zero-height wrapper, and Tauri only
         drags when the tagged element is the click's own target. -->
    <paper-shell-bar
      imDesktopDragRegion="paper-glass-bar"
      [class.shell-bar--immersive]="immersiveCanvas()"
      [class.shell-bar--canvas-pending]="canvasPending()"
      [attr.data-theme]="chromePaper()"
      [workspace]="navWorkspace()"
      [workspaces]="navWorkspaces()"
      [views]="navViews()"
      [context]="navContext()"
      [history]="navHistory()"
      [activeViewId]="activeNavViewId()"
      [tenantName]="tenantName()"
      [tenantMark]="tenantMark()"
      [location]="barLocation"
      [time]="chipTime()"
      [locationExpanded]="chip.openSegment() === 'location'"
      [timeExpanded]="chip.openSegment() === 'time'"
      (locationRequested)="onChipSegment('location', $event)"
      (timeRequested)="onChipSegment('time', $event)"
      [showSearch]="false"
      [showAi]="false"
      [showNotifications]="false"
      [variant]="currentGlassVariant()"
      [reveal]="navReveal()"
      [paletteOpen]="cmdk.open()"
      [menuFootnote]="'shell.app_shell.workspace_menu_footnote' | transloco"
      (workspaceSelected)="onWorkspaceSelected($event)"
      (viewSelected)="onNavViewSelected($event)"
      (backRequested)="onBackRequested()"
      (contextRootSelected)="onContextRootSelected()"
      (siblingSelected)="onSiblingSelected($event)"
      (lockupSelected)="onLockupSelected()"
      (shortcutsRequested)="onShortcutsRequested()">

      <!-- Brand mark — the division logo, or its initials when there is no
           logo. Non-interactive: the lockup button around it owns the click
           (and an interactive descendant of an aria-hidden span would be an
           axe violation). Suppressed inside the admin scope so the
           AdminShell's rail-mounted <im-admin-brand-block> stays the sole
           brand cap on /settings/* (B11.2) — see the SCSS that hides the
           whole lockup there. -->
      <span shell-bar-mark class="shell-brand-mark">
        @if (tenantLogo(); as logo) {
          <img class="shell-brand-logo" [src]="logo" alt="" />
        } @else {
          {{ tenantMark() }}
        }
      </span>

      <!-- Utility cluster — projected one control per node so each is a
           direct child of the organism's cluster and inherits its spacing
           and its "never shed a control" rule. Order: location chip (Paper's
           own, from `[location]` above, so it is not projected here) → search
           → AI → notifications → avatar.

           THE ORGANISATION SWITCHER IS GONE FROM THE BAR, and that is the
           point rather than a tidy-up. It hung off an unlabelled building
           mark one slot from the site chip, which is a second building icon
           switching a different thing — one changes which BUILDING you are
           looking at, the other changes which CUSTOMER you are signed into,
           and nothing in the bar said so. `41-profile-panel` already draws
           the answer: "Switch workspace" is a row of the profile menu, next
           to Admin settings and Sign out, where changing tenant belongs.
           That row is now the only entry point. Everything the header
           control did — the search box above five workspaces, the PII-safe
           search event, the customer-scoped storage purge on switch — moved
           WITH it; see `profile-panel.component.html`. -->
      <!-- The live session that follows you (Flow direction 2.2): the pill and its live card, first in the
           cluster, on every route except that session's own notes page. It draws nothing (its host is
           display: contents) while there is no live session, so the cluster keeps its spacing. -->
      <im-flow-session-indicator shell-bar-utility placement="bar" />
      <span shell-bar-utility #searchPillAnchor class="search-pill-anchor">
        <paper-search-pill
          [placeholder]="(isInAdmin() ? 'shell.top_bar.search_admin' : 'shell.top_bar.search') | transloco"
          (clicked)="openCommandPalette(searchPillAnchor)" />
      </span>
      <!-- The Ask door, restored to the bar beside search. It was removed
           when Command K's Ask row and `⌘J` became the entry points, and both
           still work — but a palette row and a chord are things you have to
           know about, and Ask now has a brand (`paper-ask-mark`) that has
           nowhere to live if the bar has no pill.

           It DOCKS the panel rather than navigating, which is where it and
           `⌘J` part company on ordinary surfaces — see
           `AiTriggerRouterService.openDockedPanel`. Gated on the `ai`
           capability because `ShellStateService.aiPanelAllowed` silently
           no-ops the open for an AI-off viewer, and a control that does
           nothing is worse than one that is not there. -->
      <!-- Not on a page with its own Ask (the Spotlight writer, 14 Oct): one Ask
           there, and ⌘J reaches it through the same router. -->
      @if (aiAvailable() && !pageOwnsAsk()) {
        <span shell-bar-utility class="ask-pill-anchor">
          <paper-ai-trigger
            variant="ask"
            [label]="'shell.ai_panel.ask' | transloco"
            [state]="askState()"
            [showKbd]="shortcutReveal.revealed()"
            (clicked)="onAskPillClicked()" />
        </span>
      }
      <!-- Global notifications bell. The PANEL is no longer a sibling here:
           §9.4 replaces the anchored dropdown with a shell-aware side panel,
           mounted in the right-panel slot above. Keeping it in the bar would
           also put a viewport-anchored surface inside a `backdrop-filter`
           ancestor, which is the containing-block trap (§1.8).
           `badgeCount` is §9.2's actionable count once the feed carries
           `requiresAction`, and today's unseen count until it does. -->
      <span shell-bar-utility class="notification-anchor">
        <paper-notification-trigger
          [badge]="inboxHosts.count()"
          (clicked)="inboxHosts.toggle('bell')" />
      </span>
      <!-- THE PROFILE POPOVER'S TRIGGER. `data-pop-trigger` is not optional
           and its absence fails silently: the band's dismissal listener runs
           on `pointerdown` in the CAPTURE phase, so an unmarked trigger is
           "anywhere else" — pressing it closes the popover and the `click`
           that follows reopens it, and the menu appears never to close
           (`POPOVERS.md` §3). It sits on the HOST here because the button is
           inside the packaged avatar component, and `closest()` walks up, so
           the host covers every pixel of it. `#profileAvatar` is what the
           band is handed as the focus-return target. -->
      <im-profile-avatar
        #profileAvatar
        shell-bar-utility
        data-pop-trigger="1" />
    </paper-shell-bar>

    <!-- ── The context chip's menu — HOST-OWNED, and portalled ────────────
         The chip (`paper-context-chip`, inside the bar's utility cluster) is
         Paper's; the menu behind it is not. Multi-select is a property of the
         SURFACE, not the switcher (§2.5) — Map picks exactly one building, a
         Schedule that compares sites would not — so the host supplies the
         panel and Paper supplies only the trigger and the `HTMLElement` to
         anchor it to.

         ⚠️ It MUST NOT be mounted inside the glass bar. A non-`none`
         `backdrop-filter` makes its element the containing block for every
         `position: fixed` descendant, so a viewport-anchored panel inside the
         bar resolves against the BAR and lands off-screen. A CDK overlay
         attaches to the body-level `cdk-overlay-container`, which is the
         portal — this template's position in the DOM is not where it renders.

         This host owns the LOCATION half — the app's one location switcher
         (`im-location-picker`), which moved here out of the map's search bar.
         The TIME half is mounted by `im-map-view` against the same anchor:
         scrubbing a moment repaints the map's desk dots, which is map
         behaviour, and the shell has no business holding it.

         It is now reachable from EVERY surface, because the chip above is.
         Nothing about the menu changed to make that true and nothing needed
         to: it was never a map component. It lists the estate from
         `PlatformDataService`, marks the current site from
         `LocationStateService` and writes the change back through
         `LocationOrchestratorService` — the same path ⌘K's "Switch to …"
         command has always taken from any page.

         KNOWN, and owed to the picker's own file (outside this lane): its
         Level section is a map concept (§2.5 gives the floor to the MAP
         chip) and it still renders off the map, where no floorplate is on
         screen to change. It should gate on the map surface.

         IT IS A POPOVER-BAND SURFACE IN A CDK PORTAL, and the overlay now
         survives ONLY as the portal — the same shape `im-time-scrubber`
         takes on the other segment of the same chip.

           - `data-popover="shell.sites"` on the picker's `.picker` root (the
             element that also carries `role="menu"` and the label, because
             the band focuses it on open) constructs the surface — fixed,
             z-90, opaque `--surface-raised`, radius, border, shadow — and
             enrols it in the band's dismissal, mutual exclusion and Escape
             ordering. `anchorFrom` inside the picker supplies top / left /
             width / the required max-height.
           - NO BACKDROP. The popover family has no scrim, and a backdrop
             swallows the very click that dismissed it — two clicks to get
             anywhere. The band's capture-phase pointerdown closes the
             popover AND lets the same click reach what it was aimed at.
           - NO `overlayKeydown`. The band owns Escape, in document capture,
             so the popover dismisses before any inspector column underneath.
             A second listener here would have made that ordering depend on
             which one Angular registered first.
           - The pane class carries `transform: none` (see
             `location-picker.scss`): `chipMenuPositions` has an `offsetY`,
             CDK writes a transform for it, and a transformed pane becomes
             the containing block for the fixed panel inside it — exactly the
             trap the portal exists to avoid.

         `data-pop-trigger` cannot be written here: the chip is inside the
         packaged `paper-shell-bar`. `MapContextChipService.open()` sets it on
         the element the chip emits.

         THE PICKER ITSELF IS MOUNTED AT THE BOTTOM OF THIS TEMPLATE, not
         here — see the popover-band block after `</div>`. -->

    <!-- Admin tab bar — deliberately NOT mapped onto the view track. These
         are browser-style document tabs (opened, closed, reordered, dirty-
         marked, middle-clickable) owned by AdminTabsService, not a fixed
         declared view set; the track is a tablist with roving tabindex and
         no close / reorder / dirty affordance. `layout="edge"` has no centre
         slot either, so the strip lands as its own full-width band directly
         beneath the bar, pinned at the offset the organism publishes. -->
    @if (showAdminTabs()) {
      <im-admin-tab-bar class="admin-tab-band" />
    }
    } @else if (!workspaceNavShown()) {
      <!-- The full-surface takeovers (AI chat, the app resource viewer) have
           no glass bar, so the window can't be dragged from the top. This
           transparent strip restores a native title-drag region on the macOS
           overlay window (hidden on web + Windows/Linux via CSS). It sits
           beneath every floating control so it never steals their clicks —
           only the empty top band moves the window. Map and World no longer
           reach this branch: Track F1 gave them the bar, and the bar is
           itself the drag region. -->
      <div class="tauri-drag-strip" data-tauri-drag-region aria-hidden="true"></div>
    }

    <!-- Urgent/critical announcement banners.
         Single .urgent-banner-area so the glass-bar top clearance is
         applied ONCE; banners stack inside without compounding it.

         The EDITORIAL critical banner still rides on every route. The
         TENANT building-alert band does not: `alertPresentation` decides,
         per section, whether the shell states these records as a band, as
         the beacon below, or not at all because the section states them
         itself. Registry data — see
         `SectionChrome.alertPresentation` for why it is not a route test
         here. -->
    @if (urgentBannerItems().length > 0 || showTenantAlertStrip()) {
      <div #urgentBannerArea class="urgent-banner-area"
           [class.urgent-banner-area--map]="isMapSurface()"
           [style.z-index]="isMapSurface() ? mapAlertZIndex : null">
        @if (urgentBannerItems().length > 0) {
          <im-critical-banner
            [items]="urgentBannerItems()"
            (read)="openUrgentArticle($event)"
            (dismiss)="articleDetail.markReadOnly($event._id)" />
        }
        @if (showTenantAlertStrip()) {
          <paper-alerts-banner
            [alerts]="tenantUrgentAlerts()"
          [compact]="true"
            [dismissible]="true"
            (expandedChange)="onTenantAlertExpanded($event)"
            (dismiss)="dismissTenantAlert($event)"
            (ctaClick)="onTenantAlertCta($event)" />
        }
      </div>
    }

    <!-- The beacon — the same records, compacted, over a full-bleed canvas.
         It owns its own float and its own portalled popover, so it is a
         SIBLING of the band's area rather than a tenant of it: the area's
         `padding-top` clearance and column stacking are the band's geometry,
         not the beacon's. -->
    @if (showAlertBeacon()) {
      <im-alert-beacon
        [alerts]="tenantUrgentAlerts()"
        [style.z-index]="mapAlertZIndex"
        (expandedChange)="onBeaconExpanded($event)"
        (dismissAlert)="dismissTenantAlert($event)"
        (ctaClick)="onTenantAlertCta($event)" />
    }

    <!-- Page content via router. Under the workspace nav a new page grows out
         of the nav control that opened it (`onRouteActivated`). -->
    <router-outlet (activate)="onRouteActivated()" />

    <!-- Bottom navigation — same two registry sources as the bar above:
         `SECTION_CHROME` (settings and profile have their own rail nav; AI
         chat and apps take the full surface) and `CHROME_TAKEOVERS` (whose
         component mounts the dock itself). See `showBottomNav()`.
         The floating glass pill chrome (position, surface, scroll-fade) lives
         inside <paper-bottom-nav /> (Theme System Revamp P3.3). -->
    @if (showBottomNav()) {
      <paper-bottom-nav
        class="ph-no-capture"
        [attr.data-theme]="chromePaper()"
        [items]="bottomNavPills()"
        [activeKey]="activeNavKey()"
        (selected)="onBottomNavSelected($event)" />
    }

  </main>

  <!-- Left sidebar — last in DOM so row-reverse places it leftmost -->
  <aside class="left-sidebar" [class.open]="shell.leftSidebarOpen()">
    <!-- The map's sidebar (layers, spaces, analytics) is the map engine's UI: it loads with the engine chunk, once the first paint has
         settled, so Home and Schedule do not download mapbox-gl and Three.js to draw a closed drawer. -->
    @defer (when engineWanted() || shell.leftSidebarOpen()) {
      @if (shell.leftSidebarOpen()) {
        <im-sidebar />
      }
    }
  </aside>

  <!-- Command palette — above everything. `im-cmdk` (SPEC-command-k.md) is
       the only one now: the legacy single-column `im-command-palette` and its
       `ShellStateService.commandPaletteOpen` signal were removed at cutover,
       so ⌘K, ⌘P, the search pill and the desktop menu all land here. It
       renders nothing at all while closed. -->
  <im-cmdk />

  <!-- Keyboard shortcuts reference dialog (⌘/) -->
  <im-shortcuts-dialog />

  <!-- Delegate picker dialog — above command palette -->
  <im-delegate-picker-dialog />

  <!-- Quick book dialog -->
  <im-quick-book-dialog />

  <!-- Beta feedback panel — shell-owned chrome over the routed surface, so it
       takes the chrome paper for the same reason the bar and the dock do
       (see `chromePaper()`). Since 4 Oct 2026 it has no floating
       button: the profile menu's Send feedback opens it (FeedbackUiService),
       on every route, Home included; closed, it draws nothing. A chrome
       takeover (game play) carries its own way out and hides it. -->
  @if (!chromeTakeover()) {
  <paper-feedback-widget [attr.data-theme]="chromePaper()" />
  }

  <!-- The Ask companion (features/ask-companion): mounted once, here, behind
       `ask-dock`. Deferred, so its chunk loads only once the flag is on; it
       decides for itself where it shows (AskCompanionService). -->
  @defer (when askCompanionFlag()) {
    <im-ask-companion />
  }

  <!-- Booking on behalf of someone: a glass pill at the top centre, under the
       search, app-wide while the mode is on, taking no room (Nath, 5 Oct).
       The schedule preview's takeover draws its own acting-as strip. -->
  <im-delegation-pill [suppressed]="isSchedulePreview()" />

</div>

<!-- ═══ THE POPOVER BAND — mounted HERE, and the position is the point ═══
     Both surfaces below are `position: fixed` and carry their own viewport
     coordinates (the picker from `anchorFrom`, the profile menu from the
     §2.4 fixed offsets). `fixed` only means "against the viewport" while NO
     ancestor is a containing block for it, and a containing block is created
     by a transform, a filter, a backdrop-filter, `will-change` or `contain`.

     Three places in this template are exactly that, which is why neither can
     live in any of them:
       - inside `paper-shell-bar` — `backdrop-filter` on the glass bar;
       - as a child of `.main-surface` — every direct child takes
         `animation: pageEnter … both`, whose final keyframe is a transform,
         so the fill mode leaves one applied forever;
       - inside a CDK overlay pane — see below.

     ⚠️ THE PICKER USED TO BE IN A `cdkConnectedOverlay` HERE, AND THAT IS
     THE BUG THAT MADE THE LOCATION CHOOSER "NOT OPEN". It did open — the
     chip emitted, the band registered, `data-pop-trigger` was set and
     `aria-expanded` flipped to `true`, so the chevron pointed up — but
     `chipMenuPositions` carried `offsetY: 8`, and CDK applies an offset by
     writing `transform: translateY(8px)` INLINE on the pane. An inline
     declaration beats a stylesheet one, so `location-picker.scss`'s
     `::ng-deep .cdk-overlay-pane.sites-panel-portal { transform: none }`
     never won. The transformed pane became the containing block, and the
     picker's correct viewport box (`left: 886px; top: 50px`) was applied as
     an offset from the pane's own origin (1258, 49.5): measured at 1512px
     wide, the panel rendered at x=2144 — fully painted, `z-index: 90`,
     `visibility: visible`, and 632px past the right edge of the screen.

     The portal is deleted rather than patched. It bought nothing the fixed
     box does not already give — `anchorFrom` is the only thing that
     positions this popover — and every fix for it (an `!important`, a
     zero-offset position array) leaves the trap armed for the next person
     who adds an offset. A plain `@if` at the template root is the shape
     `POPOVERS.md` §2.1 describes: fixed, plus a measured rect. -->

<!-- The picker drives the map engine (campus mode, building switching), so it is @defer'd with the engine chunk and loads on
     idle after the first paint. The `@if` stays INSIDE the block, directly around the picker, so its enter and leave
     animations still hang off the `@if` that adds and removes it. -->
@defer (when engineWanted() || chip.openSegment() === 'location') {
  @if (chip.openSegment() === 'location') {
    <!-- Pops out of the chip that opened it and recedes back into it (the
         motion kit, `within` the fixed plate so the host never transforms). -->
    <im-location-picker [panel]="true"
      (closed)="chip.close()"
      (animate.enter)="onLocationPopoverEnter($event)"
      (animate.leave)="onLocationPopoverLeave($event)" />
  }
}

<!-- The profile menu — a POPOVER, not a column (`POPOVERS.md` §1). Its
     `data-popover` root, its fixed §2.4 offsets and its plate all live in
     the component; the shell owns only the gate and the band registration
     (`registerProfilePopover()`), because the shell is what holds the
     trigger element focus has to return to. -->
@if (profilePopoverOpen()) {
  <im-profile-panel
    class="pp-kit-motion"
    (animate.enter)="onProfilePopoverEnter($event)"
    (animate.leave)="onProfilePopoverLeave($event)" />
}

<!-- Persistent audio player bar — outside shell flex, fixed to bottom -->
<im-audio-bar />

<!-- Project detail panel — overlay-style; opened via
     ShellStateService.openProjectDetail(projectId) -->
<im-project-detail-panel />

<!-- What's New (release notes) panel — same overlay pattern; opened
     via ShellStateService.openWhatsNew() from the feedback widget,
     the command palette, or any future surface. -->
<im-whats-new-panel />

<!-- Schedule V2 — minimised meeting drafts dock. Persists across
     navigation; chips fed from MeetingDraftService (server-backed
     with a localStorage write-through cache). The "View all" overflow
     opens the room schedule's Drafts panel (navigating there first). -->
@if (!chromeTakeover()) {
<bmp-draft-dock
  [drafts]="draftDockEntries()"
  (restore)="onDraftRestore($event)"
  (discard)="onDraftDiscard($event)"
  (showLibrary)="onShowDraftsLibrary()" />

<!-- Drafts library side panel — slide-in surface listing every saved
     draft so the user can recall any of them, not just the three the
     dock surfaces. Opens for the retiring Book a Meeting panel's own
     "drafts" link (MeetingDraftService.openLibrary()); the dock now
     sends people to the schedule's Drafts panel instead. -->
<bmp-drafts-library />
}

<!-- First-run push notification primer — non-modal card in the bottom-right
     corner of the MAIN app shell (the AdminShell at /settings|/admin uses its
     own layout, so this never appears there). Self-gates on the show
     conditions in push-primer.logic.ts; inert until they hold. -->
<im-push-primer />

<!-- Screen-reader announcement of the assistant's answer. Mounted HERE, once,
     rather than in the docked panel and the /ai page: the conversation
     service is root-provided and both surfaces can be alive at once, so a
     region per surface would read one answer twice. Renders an empty
     visually-hidden paragraph until the assistant says something. -->
<im-ai-live-region />

<!-- "Back to chat" continuity chip — floats above the bottom nav on the
     map after the full-screen Workplace AI chat jumps the user out
     (view-on-map). Self-gating; renders nothing when inactive. -->
<im-ai-return-chip />
<paper-toast-stack />

<!-- Unified Book Meeting Panel — mounted ONCE at shell level so the
     minimised-draft chip + drafts library + any deep-link / service
     caller can restore or open a meeting from any route. The panel
     self-gates on BookMeetingService.visible() so it's inert until
     opened. -->
<im-book-meeting-panel />

<!-- THE meeting EDITOR (the schedule redesign's sp-meeting-expanded) —
     mounted ONCE at shell level so every surface that creates or edits a
     meeting opens the SAME card, via MeetingComposerService.open(prefill).
     Inert until opened, and currently EMPTY: slice 1 of the extraction
     wires the service, the layer, the seam and the theme; the editor
     state still lives in schedule-preview.component.ts until
     indoor_mapping_poc-e4sb.2 moves it. -->
<im-meeting-composer-host />

<!-- THE meeting panel over any page (MeetingPanelService.open): the
     schedule's own panel — card, editor, sizes, Escape — mounted panel-only
     off the schedule page while it is open, and gone when it closes. -->
<im-meeting-panel-host />
<!-- A room, desk or asset over any page, with a mini map and Find on map (SpacePanelService). It scopes its own orchestrator
     over the map engine's (`LocationOrchestratorService`), so it loads with the engine chunk once the first paint has settled.
     A request made before then waits for the block: the host reads the open request when it mounts. -->
@defer (when engineWanted() || spacePanels.isOpen()) {
  <im-space-panel-host />
}

<!-- THE issue panel over any page (IssuePanelService.open), beside the open column so Ask stays on screen. An
     issue's place opens the space panel above (openSpacePanel → im-space-panel-host). Home opens issues in its
     own column. -->
<im-issue-panel-host [style.--issue-panel-clear]="issuePanelClear()" />

<!-- THE task view — sibling of the meeting view: clicking a task on any
     page (home My Day / My Week, redesign calendar) opens the SAME
     right-slide task panel via TaskDetailService.open(). Its Edit
     escalates into the full im-task-dialog editor, layered above. -->
<im-task-detail-host />
<im-desk-slot-change-host />

<!-- PRESSED NAV — one light travels the pressed pill's own glyph, once.
     The prototype puts this INSIDE the button; `paper-view-track` renders the
     pills and projects nothing, so it is an overlay that measures the tab and
     draws over it. Read `components/nav-trace/nav-trace.ts` before moving it.

     `@for` rather than `@if`: the mount IS the animation trigger, so a second
     press has to produce a second element. Swapping the inputs of the one
     already there would leave a stylesheet animation mid-flight. -->
@for (trace of navTraces(); track trace.key) {
  <im-nav-trace [icon]="trace.icon" [rect]="trace.rect" />
}

<!-- Flow's live recording, floating over every page but its own live page: a movable glass capsule with Pause,
     Stop and the latest lines, which pops out into an always-on-top window where the browser can
     (im-flow-recording-widget). It also says when the viewer records on another device or tab. -->
<im-flow-recording-widget />

<!-- The caption corner (WP-31): the newest line of the live session, in the language the person reads, on every route
     while they are in it (bottom-left; a strip above the Ask pill on a phone). It publishes `--flow-caption-reserve`
     on <html>; the main surface below reads it on pages that are not the session page (app-shell.component.scss). -->
<im-flow-caption-corner />

@if (onboardingComplete() && !isPreviewHarness()) { <im-walkthrough-host /> }
